/**
 * 二項比率（コンバージョン率）の検定と区間推定。
 *
 * 方針:
 *  - 仮説検定 (p 値) は帰無仮説 H0: p1 = p2 と整合する **プールした標準誤差** を使う。
 *  - 差の信頼区間は各群を独立に推定する **プールしない標準誤差** を使う。
 *    区間推定では H0 を仮定しないため、こちらが正しい。
 *  - 各群単体の区間は、少数サンプルでも破綻しない Wilson スコア区間を使う。
 *
 * p 値と信頼区間で標準誤差が異なるため、有意水準ぎわで「p < 0.05 なのに
 * 差の信頼区間が 0 をまたぐ」という食い違いが起こりうる。これは誤りではなく
 * 両者が別の問いに答えているためで、UI 側で注記する。
 */

import { normalCdf, normalQuantile, normalSf, erfc } from './normal'
import type { Interval, Observation, RateEstimate, Tail } from './types'

/**
 * 信頼水準から有意水準を求める。
 *
 * 1 - 0.95 は倍精度では 0.050000000000000044 になり、p 値がちょうど 0.05 の
 * ケースが有意側に転ぶ。判定境界そのものがこのツールの主題なので、
 * 利用者が選ぶ丸い信頼水準に対しては厳密な alpha を返す。
 */
export function alphaFromConfidence(confidence: number): number {
  return Math.round((1 - confidence) * 1e12) / 1e12
}

/** 信頼水準から両側/片側に応じた臨界値 z を返す。 */
export function criticalValue(confidence: number, tail: Tail): number {
  const alpha = alphaFromConfidence(confidence)
  return tail === 'two-sided'
    ? normalQuantile(1 - alpha / 2)
    : normalQuantile(1 - alpha)
}

/**
 * Wilson スコア信頼区間。
 * 正規近似 (Wald) 区間と違い、比率が 0 や 1 に近くても区間が [0, 1] を出ない。
 */
export function wilsonInterval(
  x: number,
  n: number,
  confidence: number,
): Interval {
  if (n <= 0) return { low: NaN, high: NaN }
  const z = normalQuantile(1 - (1 - confidence) / 2)
  const p = x / n
  const z2 = z * z
  const denominator = 1 + z2 / n
  const center = (p + z2 / (2 * n)) / denominator
  const margin =
    (z / denominator) * Math.sqrt((p * (1 - p)) / n + z2 / (4 * n * n))
  // x = 0 のとき下限は、x = n のとき上限は、厳密に 0 / 1 になる。
  // 計算式のまま引き算すると浮動小数点の残差 (1e-18 程度) が残るため境界を固定する。
  return {
    low: x <= 0 ? 0 : Math.max(0, center - margin),
    high: x >= n ? 1 : Math.min(1, center + margin),
  }
}

/** 1 群の推定結果を組み立てる。 */
export function estimateRate(
  observation: Observation,
  confidence: number,
): RateEstimate {
  const { n, x } = observation
  return {
    n,
    x,
    rate: n > 0 ? x / n : NaN,
    interval: wilsonInterval(x, n, confidence),
  }
}

/** z 統計量から選択した裾に応じた p 値を返す。 */
export function pValueFromZ(z: number, tail: Tail): number {
  switch (tail) {
    case 'two-sided':
      // 2 * normalSf(|z|) と等価だが、erfc を直接使うほうが裾で精度が高い
      return Math.min(1, erfc(Math.abs(z) / Math.SQRT2))
    case 'greater':
      return normalSf(z)
    case 'less':
      return normalCdf(z)
  }
}

export interface ComparisonOptions {
  confidence: number
  tail: Tail
  /**
   * 多重比較補正後の有意水準。指定した場合、信頼区間の臨界値もこの水準で計算する。
   * 省略時は confidence から算出する。
   */
  adjustedAlpha?: number
}

export interface Comparison {
  control: RateEstimate
  variant: RateEstimate
  /** variant の比率 - control の比率 */
  absoluteDiff: number
  absoluteDiffInterval: Interval
  /** 相対リフト (variant / control - 1)。control の比率が 0 なら null。 */
  relativeLift: number | null
  /** 相対リフトの信頼区間。どちらかの成功数が 0 なら計算できないため null。 */
  relativeLiftInterval: Interval | null
  /** 検定統計量。両群の比率が同一で分散 0 の縮退ケースでは null。 */
  z: number | null
  pValue: number | null
  pooledStandardError: number
  unpooledStandardError: number
  criticalValue: number
}

/**
 * 2 群の比率の差の検定。
 *
 * @param control 対照群
 * @param variant 比較したい群
 */
export function compareProportions(
  control: Observation,
  variant: Observation,
  options: ComparisonOptions,
): Comparison {
  const { confidence, tail } = options
  const alpha = options.adjustedAlpha ?? alphaFromConfidence(confidence)
  const effectiveConfidence = 1 - alpha
  const zCrit = criticalValue(effectiveConfidence, tail)

  const c = estimateRate(control, effectiveConfidence)
  const v = estimateRate(variant, effectiveConfidence)

  const p1 = c.rate
  const p2 = v.rate
  const n1 = c.n
  const n2 = v.n

  // 帰無仮説のもとでの標準誤差（検定用）
  const pooledRate = (c.x + v.x) / (n1 + n2)
  const pooledSe = Math.sqrt(
    pooledRate * (1 - pooledRate) * (1 / n1 + 1 / n2),
  )

  // 各群独立の標準誤差（区間推定用）
  const unpooledSe = Math.sqrt(
    (p1 * (1 - p1)) / n1 + (p2 * (1 - p2)) / n2,
  )

  const absoluteDiff = p2 - p1

  // 両群とも全員成功 / 全員失敗だと分散が 0 になり z が定義できない
  const z = pooledSe > 0 ? absoluteDiff / pooledSe : null
  const pValue = z === null ? null : pValueFromZ(z, tail)

  const absoluteDiffInterval = buildInterval(
    absoluteDiff,
    zCrit * unpooledSe,
    tail,
  )

  const relativeLift = p1 > 0 ? p2 / p1 - 1 : null
  const relativeLiftInterval = logRelativeRiskInterval(c, v, zCrit, tail)

  return {
    control: c,
    variant: v,
    absoluteDiff,
    absoluteDiffInterval,
    relativeLift,
    relativeLiftInterval,
    z,
    pValue,
    pooledStandardError: pooledSe,
    unpooledStandardError: unpooledSe,
    criticalValue: zCrit,
  }
}

/** 点推定値と誤差幅から、裾に応じた区間を組み立てる。 */
function buildInterval(estimate: number, margin: number, tail: Tail): Interval {
  switch (tail) {
    case 'two-sided':
      return { low: estimate - margin, high: estimate + margin }
    case 'greater':
      return { low: estimate - margin, high: Infinity }
    case 'less':
      return { low: -Infinity, high: estimate + margin }
  }
}

/**
 * 相対リフトの信頼区間。
 *
 * 比率の比 (relative risk) は分布が右に歪むため、対数スケールで区間を作って
 * 戻す。delta 法により Var(log p̂) ≈ (1 - p) / x。
 */
function logRelativeRiskInterval(
  control: RateEstimate,
  variant: RateEstimate,
  zCrit: number,
  tail: Tail,
): Interval | null {
  if (control.x <= 0 || variant.x <= 0) return null
  if (control.rate <= 0 || variant.rate <= 0) return null

  const logRr = Math.log(variant.rate / control.rate)
  const seLogRr = Math.sqrt(
    (1 - control.rate) / control.x + (1 - variant.rate) / variant.x,
  )
  const logInterval = buildInterval(logRr, zCrit * seLogRr, tail)
  return {
    low: Math.exp(logInterval.low) - 1,
    high: Math.exp(logInterval.high) - 1,
  }
}
